import type {
  ChangeEventHandler,
  Dispatch,
  FocusEventHandler,
  FormEvent,
  InputHTMLAttributes,
  ReactNode,
  RefObject,
  SetStateAction,
} from 'react'
import { createRef, useEffect, useMemo, useState } from 'react'
import Debug from 'debug'
import { t } from '@lingui/core/macro'

import { objectKeys } from '@jotta/types/TypeUtils'

const debug = Debug('form')

type ValidationError = { message: string; view: ReactNode } | string | undefined
export type ValidationResult = Promise<ValidationError> | ValidationError

type FieldTypeMap = {
  text: string
  email: string
  number: number
  boolean: boolean
}
type FieldTypeKey = keyof FieldTypeMap

type FieldSpec<FIELD extends FieldSpec<FIELD>> = {
  type: FieldTypeKey
  placeholder?: string
  required?: boolean | string
  onChange?: (v: FieldTypeMap[FIELD['type']]) => void
  validate?: (v?: FieldTypeMap[FIELD['type']]) => ValidationResult
  autoComplete?: string
  focusOnMount?: boolean
}

type FieldsSpec<FIELDS extends FieldsSpec<FIELDS>> = Record<
  keyof FIELDS,
  FieldSpec<any>
>

export type FieldError = {
  message: string
  view: ReactNode
  displayInSummary: boolean
}

type FieldState = {
  ref: RefObject<HTMLInputElement>
  value?: any
  error?: FieldError
  valid: boolean
  needValidate: boolean
}

export type FormData<FIELDS extends FieldsSpec<FIELDS>> = Partial<{
  [FIELD in keyof FIELDS]: FieldTypeMap[FIELDS[FIELD]['type']]
}>

type FormState<FIELDS extends FieldsSpec<FIELDS>> = Partial<{
  [FIELD in keyof FIELDS]: FieldState
}>

export type InputProps = {
  ref: RefObject<HTMLInputElement>
  name: string
  value: string
  checked: boolean
  placeholder: string | undefined
  onChange: ChangeEventHandler<HTMLInputElement>
  onBlur: FocusEventHandler<HTMLInputElement>
  autoComplete: string | undefined
  'aria-errormessage': string | undefined
  'aria-invalid': boolean
  inputMode: InputHTMLAttributes<HTMLInputElement>['inputMode']
}

export type Field<FIELD extends FieldSpec<FIELD>> = {
  inputProps: InputProps
  spec: FieldSpec<FIELD>
  formSpec: FormSpec<any>
  value: FieldTypeMap[FIELD['type']]
  set: (value: FieldTypeMap[FIELD['type']] | undefined) => void
  validate: () => void
  valid: boolean
  error?: FieldError
}

type Fields<FIELDS extends FieldsSpec<FIELDS>> = {
  [FIELD in keyof FIELDS]: Field<FIELDS[FIELD]>
}

type ReactState<T extends FieldsSpec<T>> = [
  FormState<T>,
  Dispatch<SetStateAction<FormState<T>>>,
]

const valueConverters: Record<FieldTypeKey, (value: string) => any> = {
  boolean: value => Boolean(value) && value !== 'false',
  number: Number,
  text: value => value,
  email: value => value,
}

function format(validationError?: ValidationError) {
  if (!validationError) {
    return
  } else if (typeof validationError === 'string') {
    return validationError
  } else {
    return validationError?.message
  }
}

class FieldImpl<FIELDS extends FieldsSpec<FIELDS>, F extends keyof FIELDS> {
  public readonly valueSpec: FieldSpec<any>
  private readonly formState: ReactState<FIELDS>
  private readonly converter: (value: string) => any

  public readonly required: boolean | string
  public readonly name: F
  public readonly onChange: ChangeEventHandler<HTMLInputElement>
  public readonly onBlur: FocusEventHandler<HTMLInputElement>

  constructor(
    formState: ReactState<FIELDS>,
    fieldName: F,
    valueSpec: FieldSpec<any>,
  ) {
    this.formState = formState
    this.name = fieldName
    this.valueSpec = valueSpec
    this.required = valueSpec.required || false
    this.converter = valueConverters[valueSpec.type]

    this.onBlur = () => this.validate()
    this.onChange = ({ target: { type, value, checked } }) => {
      if (type === 'checkbox') {
        this.setValue(checked ? 'true' : undefined)
      } else {
        this.setValue(value)
      }
    }

    if (this.state.needValidate) {
      this.validate()
      this.setState({
        needValidate: false,
      })
    }
  }

  public get state() {
    return this.formState[0][this.name] as FieldState
  }

  public get ref() {
    return this.state?.ref
  }

  public get value() {
    return this.state?.value
  }

  public get valid() {
    return this.state?.valid
  }

  public get error(): FieldError | undefined {
    return this.state?.error
  }

  public setValue(value?: string) {
    const convertedValue =
      value !== undefined ? this.converter(value) : undefined
    this.setState({
      error: undefined,
      value: convertedValue,
      valid: false,
    })
    const specOnChange = this.valueSpec.onChange
    if (specOnChange) {
      setTimeout(() => specOnChange(convertedValue))
    }
  }

  public setState(state: Partial<FieldState>) {
    debug(
      `set ${String(this.name)}: ${state?.value} ${format(state?.error?.message)}`,
    )
    this.formState[1](prevState => ({
      ...prevState,
      [this.name]: {
        ...prevState[this.name],
        ...state,
      },
    }))
  }

  public async validate() {
    let validationError: FieldError | undefined = undefined

    const { validate, required } = this.valueSpec
    const value = this.value
    const missingValue = value === undefined || value === ''
    if (required && missingValue) {
      const isString = typeof required === 'string'
      const message = isString ? required : t`This field is required`
      validationError = {
        message,
        view: message,
        displayInSummary: isString,
      }
    }

    let validationResultError: ValidationError
    try {
      const validationResult = validate && validate(value)
      validationResultError =
        validationResult instanceof Promise
          ? await validationResult
          : validationResult
    } catch (e) {
      reportError(e)
      validationResultError = `${e}`
    }

    if (validationResultError) {
      const intermediateError =
        typeof validationResultError === 'string'
          ? {
              message: validationResultError,
              view: validationResultError,
            }
          : validationResultError

      validationError = {
        ...intermediateError,
        displayInSummary: true,
      }
    }

    if (validationError) {
      this.setState({
        error: validationError,
      })
    } else if (!missingValue) {
      this.setState({
        valid: true,
      })
    }

    return validationError
  }
}

type FormStatus = 'idle' | 'validating' | 'invalid' | 'submitting'
export type FormError = string | Error
export type Form<T extends FieldsSpec<T>> = Fields<T> & {
  set: <KEY extends keyof T, VALUE extends FieldTypeMap[T[KEY]['type']]>(
    fieldName: KEY,
    value: VALUE,
  ) => void
  onReset: () => void
  onSubmit: (e: FormEvent) => void
  isSubmitting: boolean
  status: FormStatus
  error?: FormError
  fields: Fields<T>
}
type FormSpec<FIELDS extends FieldsSpec<FIELDS>> = {
  fields: FIELDS
  suppressIndicatorsForRequiredFields?: boolean
  validate?: (data: FormData<FIELDS>) => ValidationResult
  submit: (data: FormData<FIELDS>) => Promise<any> | void
  reset?: () => void
  resetOnSuccess?: boolean
  defaultValues?: () => FormData<FIELDS & {}>
}
export function useForm<FIELDS extends FieldsSpec<FIELDS>>(
  formSpec: FormSpec<FIELDS>,
): Form<FIELDS> {
  const {
    submit,
    defaultValues,
    fields: fieldsSpec,
    reset,
    resetOnSuccess,
    validate,
  } = formSpec
  const [status, setStatus] = useState<FormStatus>('idle')
  const [error, setError] = useState<FormError>()

  const state = useState<FormState<FIELDS>>(() => {
    const defaultState: FormState<FIELDS> = {}
    const defaults = defaultValues ? defaultValues() : defaultState
    return objectKeys(fieldsSpec).reduce((acc, fieldName) => {
      return {
        ...acc,
        [fieldName]: {
          ref: createRef(),
          value: defaults[fieldName],
          needValidate: !!defaults[fieldName],
        },
      }
    }, defaultState)
  })

  const fields = useMemo(() => {
    return Object.entries(fieldsSpec as FieldsSpec<any>).map(
      ([fieldName, valueSpec]) =>
        new FieldImpl<FIELDS, any>(state, fieldName, valueSpec),
    )
  }, [fieldsSpec])

  const focusOnMountRef = fields
    .filter(field => field.valueSpec.focusOnMount)
    .map(value => value.ref?.current)[0]

  useEffect(() => {
    if (focusOnMountRef) {
      focusOnMountRef.focus()
    }
  }, [focusOnMountRef])

  const inputs = useMemo(() => {
    return fields.reduce((acc, field): Fields<FIELDS> => {
      const { value, valid, state, onBlur, onChange, valueSpec, error, name } =
        field
      const { placeholder, type, autoComplete } = valueSpec
      const errorMessage = error?.message
      return {
        ...acc,
        [name]: {
          value,
          valid,
          error,
          formSpec,
          spec: valueSpec,
          set: field.setValue.bind(field),
          validate: field.validate.bind(field),
          inputProps: {
            name,
            value: value ? String(value) : '',
            ref: state.ref,
            checked: Boolean(value),
            placeholder,
            onChange,
            onBlur,
            autoComplete: autoComplete || 'off',
            inputMode: type === 'email' ? 'email' : undefined,
            'aria-errormessage': errorMessage,
            'aria-invalid': Boolean(errorMessage),
          },
        } satisfies Field<any>,
      }
    }, {} as Fields<FIELDS>)
  }, [fields])

  const onReset = () => {
    debug('onReset')
    setError(undefined)
    fields.forEach(field => field.setValue(undefined))
    reset?.()
  }
  return {
    ...inputs,
    status,
    error,
    fields: inputs,
    isSubmitting: status !== 'idle' && status !== 'invalid',
    set: (fieldName, value) => inputs[fieldName].set(value),
    onReset: onReset,
    onSubmit: async (e: FormEvent) => {
      setError(undefined)
      let valid = true
      try {
        e.preventDefault()
        debug('onSubmit')

        setStatus('validating')
        for (const field of fields) {
          const errorMessage = await field.validate()
          if (valid && errorMessage) {
            field.ref?.current?.focus()
            valid = false
          }
        }
        debug('onSubmit valid:', valid)
        if (valid) {
          const formData = fields.reduce(
            (acc, field) => ({
              ...acc,
              [field.name]: field.value,
            }),
            {} as FormData<FIELDS>,
          )
          const errorMessage = validate && (await validate(formData))
          if (errorMessage) {
            setError(
              typeof errorMessage === 'string'
                ? errorMessage
                : errorMessage.message,
            )
          } else {
            setStatus('submitting')
            debug('submitting', formData)
            await submit(formData)
            if (resetOnSuccess) {
              onReset()
            }
          }
        }
      } catch (error) {
        setError(error instanceof Error ? error : new Error(error?.toString()))
        throw error
      } finally {
        setStatus(valid ? 'idle' : 'invalid')
      }
    },
  }
}
